import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as schoolService from '../services/schoolService';

const useSchoolStudents = (seasonId, year) => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  const loadStudents = useCallback(async () => {
    if (!seasonId) return;
    try {
      setLoading(true);
      const data = await schoolService.getSeasonStudents(seasonId);
      setStudents(data);
      // Auto-select first student if none selected
      if (data.length > 0 && !selectedStudentId) {
        setSelectedStudentId(data[0].id);
      }
    } catch (err) {
      console.error('❌ Error loading students:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const addStudent = useCallback(async () => {
    if (!seasonId || !user) return null;
    try {
      const newReg = await schoolService.createStudentRegistration(seasonId, user.id, year);
      const newStudent = { id: newReg.id, status: 'draft', parent_consent_status: 'not_required', created_at: newReg.created_at, dancer_information: null };
      setStudents(prev => [...prev, newStudent]);
      setSelectedStudentId(newReg.id);
      return newReg.id;
    } catch (err) {
      console.error('❌ Error adding student:', err);
      setError(err.message);
      return null;
    }
  }, [seasonId, user, year]);

  const saveStudent = useCallback(async (registrationId, studentData) => {
    if (!registrationId) return false;
    try {
      const saved = await schoolService.saveStudentInfo(registrationId, studentData, year);

      // Mirror what the service actually persisted. This used to guess at the
      // status locally — which is how it could read 'pending' on screen while
      // the database still said 'not_required', until the next reload.
      setStudents(prev => prev.map(s => (
        s.id === registrationId
          ? { ...s, dancer_information: saved, parent_consent_status: saved.parent_consent_status }
          : s
      )));

      return true;
    } catch (err) {
      console.error('❌ Error saving student:', err);
      setError(err.message);
      return false;
    }
  }, [year]);

  const updateStudentConsentStatus = useCallback((registrationId, status) => {
    setStudents(prev => prev.map(s =>
      s.id === registrationId ? { ...s, parent_consent_status: status } : s
    ));
  }, []);

  const deleteStudent = useCallback(async (registrationId) => {
    try {
      await schoolService.deleteStudentRegistration(registrationId);
      setStudents(prev => {
        const remaining = prev.filter(s => s.id !== registrationId);
        if (selectedStudentId === registrationId) {
          setSelectedStudentId(remaining.length > 0 ? remaining[0].id : null);
        }
        return remaining;
      });
      return true;
    } catch (err) {
      console.error('❌ Error deleting student:', err);
      setError(err.message);
      return false;
    }
  }, [selectedStudentId]);

  const saveVariations = useCallback(async (registrationId, variationsArray) => {
    try {
      const existing = await schoolService.getStudentVariations(registrationId);
      const keep = new Set(variationsArray.map(v => v.variation_number));
      const toDelete = existing.filter(v => !keep.has(v.variation_number));
      await Promise.all([
        ...variationsArray.map(v => schoolService.saveStudentVariation(registrationId, v.variation_number, v)),
        ...toDelete.map(v => schoolService.deleteStudentVariation(registrationId, v.variation_number))
      ]);
      return true;
    } catch (err) {
      console.error('❌ Error saving variations:', err);
      setError(err.message);
      return false;
    }
  }, []);

  const getStudentVariations = useCallback(async (registrationId) => {
    return schoolService.getStudentVariations(registrationId);
  }, []);

  const deleteVariation = useCallback(async (registrationId, variationNumber) => {
    return schoolService.deleteStudentVariation(registrationId, variationNumber);
  }, []);

  return {
    students,
    loading,
    error,
    selectedStudentId,
    setSelectedStudentId,
    addStudent,
    saveStudent,
    deleteStudent,
    saveVariations,
    getStudentVariations,
    deleteVariation,
    updateStudentConsentStatus,
    reload: loadStudents
  };
};

export default useSchoolStudents;
